import api from "../services/authService";

/**
 * Document collections: named, matter-scoped groups of PDFs prepared and
 * questioned as one unit.
 *
 * The per-document surface in `aiPrepare.js` answers "what does THIS PDF
 * say". A claim file's evidence is rarely one PDF — post-service medical
 * records arrive as a dozen separate scans — so a collection is the
 * durable handle for the group: one target for bulk prepare, and a stable
 * scope key so the conversation survives the browser session.
 *
 * Backend surface (dms_backend PR #1477):
 *   GET  /api/matters/<matter_id>/document-collections/   list
 *   POST /api/matters/<matter_id>/document-collections/   create (optionally
 *        with `prepare: true`, which creates and kicks off prepare in one
 *        call so a dropped response cannot leave a collection created but
 *        unprepared)
 *   GET/PATCH/DELETE /api/document-collections/<id>/      detail
 *   POST/DELETE      /api/document-collections/<id>/documents/  membership
 *   GET  /api/document-collections/<id>/ai-readiness/     group readiness
 *   POST /api/document-collections/<id>/ai-prepare/       prepare the group
 *   POST /api/documents/ai-prepare/bulk/                  prepare a raw
 *        selection without saving a group
 *
 * Readiness `state` is the aggregate the UI gates on: `ready` when every
 * member is conversable, `partial` when only some are (chat still works,
 * scoped to those), `preparing` while work is in flight and nothing is
 * ready yet, `not_ready`, or `empty`.
 */

export const fetchMatterCollections = async (matterId) => {
  const response = await api.get(
    `/matters/${matterId}/document-collections/`
  );
  return response.data;
};

export const createCollection = async ({
  matterId,
  name,
  documentIds = [],
  description = "",
  prepare = false,
}) => {
  const response = await api.post(
    `/matters/${matterId}/document-collections/`,
    {
      name,
      description,
      document_ids: documentIds.map(String),
      prepare: Boolean(prepare),
    }
  );
  return response.data;
};

export const fetchCollection = async (collectionId) => {
  const response = await api.get(`/document-collections/${collectionId}/`);
  return response.data;
};

export const renameCollection = async (collectionId, { name, description }) => {
  const response = await api.patch(`/document-collections/${collectionId}/`, {
    ...(name === undefined ? {} : { name }),
    ...(description === undefined ? {} : { description }),
  });
  return response.data;
};

export const deleteCollection = async (collectionId) => {
  await api.delete(`/document-collections/${collectionId}/`);
};

export const addCollectionDocuments = async (collectionId, documentIds) => {
  const response = await api.post(
    `/document-collections/${collectionId}/documents/`,
    { document_ids: (documentIds || []).map(String) }
  );
  return response.data;
};

export const removeCollectionDocuments = async (collectionId, documentIds) => {
  // axios sends a DELETE body only under `data`.
  const response = await api.delete(
    `/document-collections/${collectionId}/documents/`,
    { data: { document_ids: (documentIds || []).map(String) } }
  );
  return response.data;
};

export const fetchCollectionReadiness = async (collectionId) => {
  const response = await api.get(
    `/document-collections/${collectionId}/ai-readiness/`
  );
  return response.data;
};

export const prepareCollection = async (collectionId) => {
  const response = await api.post(
    `/document-collections/${collectionId}/ai-prepare/`
  );
  return response.data;
};

export const bulkPrepareDocuments = async (documentIds) => {
  const response = await api.post(`/documents/ai-prepare/bulk/`, {
    document_ids: (documentIds || []).map(String),
  });
  return response.data;
};
